import { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import { useExpenses, useCreateExpense, useDeleteExpense } from '../../hooks/api/useExpenses'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'
import PaymentAccountPicker from '../../components/ui/PaymentAccountPicker'
import {
  EXPENSE_CATEGORIES,
  ExpenseCategory,
  Expense,
} from '../../services/expenses/expenseService'

type ChipVariant = 'success' | 'info' | 'warn' | 'danger' | 'neutral'
const CATEGORY_VARIANTS: Record<ExpenseCategory, ChipVariant> = {
  fuel:      'warn',
  lunch:     'info',
  utilities: 'neutral',
  repair:    'danger',
  payroll:   'neutral',
  other:     'neutral',
}

const ExpensesPage = () => {
  const [from, setFrom]   = useState<Date | null>(subDays(new Date(), 30))
  const [to, setTo]       = useState<Date | null>(new Date())
  const [page, setPage]   = useState(0)
  const [filterCategory, setFilterCategory] = useState('')
  const [openDialog, setOpenDialog]   = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const [form, setForm] = useState({
    category: '' as ExpenseCategory | '',
    amount:   '',
    date:     format(new Date(), 'yyyy-MM-dd'),
    description: '',
    source: 'cash',
  })

  const fmt = (d: Date | null) => (d ? format(d, 'yyyy-MM-dd') : undefined)

  const { data, isLoading } = useExpenses({
    from:     fmt(from),
    to:       fmt(to),
    category: filterCategory || undefined,
    page:     page + 1,
  })

  const createMutation = useCreateExpense()
  const deleteMutation = useDeleteExpense()

  const handleCreate = () => {
    if (!form.category || !form.amount) return
    createMutation.mutate(
      {
        category:    form.category as ExpenseCategory,
        amount:      parseFloat(form.amount),
        date:        form.date,
        description: form.description,
        payment_account: form.source === 'cash' ? 'cash' : 'bank',
        ...(form.source !== 'cash' && { bank_account_id: form.source }),
      },
      {
        onSuccess: () => {
          setOpenDialog(false)
          setForm({ category: '', amount: '', date: format(new Date(), 'yyyy-MM-dd'), description: '', source: 'cash' })
        },
      }
    )
  }

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, { onSuccess: () => setDeleteTarget(null) })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4">Expenses</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Add Expense
          </Button>
        </Box>

        {/* Summary */}
        {data && (
          <Paper sx={{ p: 2.5, mb: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Expenses</Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">
                {formatCurrency(data.info.total_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Entries</Typography>
              <Typography variant="h5" fontWeight="bold">{data.info.total}</Typography>
            </Box>
          </Paper>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <DatePicker
            label="From"
            value={from}
            onChange={(v) => { setFrom(v); setPage(0) }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="To"
            value={to}
            minDate={from ?? undefined}
            onChange={(v) => { setTo(v); setPage(0) }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <TextField
            select
            label="Category"
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(0) }}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            {EXPENSE_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Table */}
        <Paper>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7f5f2' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Recorded By</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.results.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                          No expenses in this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.results.map((exp: Expense) => (
                        <TableRow key={exp._id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {format(new Date(exp.date), 'dd MMM yyyy')}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={exp.category.charAt(0).toUpperCase() + exp.category.slice(1)}
                              variant={CATEGORY_VARIANTS[exp.category]}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {exp.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {formatCurrency(exp.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {typeof exp.recorded_by === 'object' ? exp.recorded_by.name : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(exp._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {data && data.info.total > 0 && (
                <TablePagination
                  component="div"
                  count={data.info.total}
                  page={page}
                  rowsPerPage={30}
                  rowsPerPageOptions={[30]}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              )}
            </>
          )}
        </Paper>

        {/* Add expense dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add Expense</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label="Category"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as ExpenseCategory }))}
              size="small"
              fullWidth
              required
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Amount (Rs)"
              type="number"
              size="small"
              fullWidth
              required
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Date"
              type="date"
              size="small"
              fullWidth
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Description (optional)"
              size="small"
              fullWidth
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <PaymentAccountPicker value={form.source} onChange={(source) => setForm((f) => ({ ...f, source }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!form.category || !form.amount || createMutation.isPending}
            >
              {createMutation.isPending ? <CircularProgress size={18} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirm dialog */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
          <DialogTitle>Delete Expense?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteTarget && handleDelete(deleteTarget)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <CircularProgress size={18} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default ExpensesPage
